/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    Checkbox,
    DetailsList,
    DetailsListLayoutMode,
    Dropdown,
    IColumn,
    Label,
    Panel,
    PanelType,
    SelectionMode,
    Stack
} from "@fluentui/react";
import { HuxTextBox } from "@nuance/hux-components";
import { HuxControlLabel } from "@nuance/hux-components";
import { IRulePanelViewModel, RulePanelCommands } from "./RulePanelViewModel";
import { IRulePanelProps } from "./IRulePanelProps";
import { IRulePanelState } from "./IRulePanelState";
import styles from "./RulePanel.module.css";
import { IRulePanelDataAppliedToSetting } from "./IRulePanelData";
import { IPanelFooterProps } from "../../../MainView/Panels/IPanelFooterProps";
import PanelFooter from "../../../MainView/Panels/PanelFooter";

const stackTokens = { childrenGap: 10 };

const rulePanelCommands = new RulePanelCommands();
const cmds = rulePanelCommands.Cmds;

const RulePanel = (props: IRulePanelProps): JSX.Element => {
    const initialState: IRulePanelState = {
        rulePanelProps: { ...props },
        errors: {},
        touched: {},
        isValid: !props.data.isNew,
        isDirty: false,
        data: { ...props.data }
    };

    const [state, dispatch] = React.useReducer(rulePanelCommands.Reducer, initialState);

    const onSaveRule = () => {
        if (props.onSaveContent != undefined) {
            const saveData = rulePanelCommands.Save(state);
            props.onSaveContent(saveData);
        }
    };

    const vm: IRulePanelViewModel = {
        state: state,
        cmds: cmds,
        dispatch: dispatch
    };

    const onRenderHidden = (item: IRulePanelDataAppliedToSetting): JSX.Element => {
        return (
            <Stack horizontalAlign="center" verticalAlign="center">
                <Checkbox
                    {...props}
                    onChange={() => dispatch(cmds.onToggleApplyToHidden(item.settingName))}
                    checked={item.isHiddenRuleApplied}
                    defaultIndeterminate={item.isHiddenRuleAppliedIndeterminant}
                />
            </Stack>
        );
    };

    const onRenderDisabled = (item: IRulePanelDataAppliedToSetting): JSX.Element => {
        return (
            <Stack horizontalAlign="center" verticalAlign="center">
                <Checkbox
                    onChange={() => dispatch(cmds.onToggleApplyToDisable(item.settingName))}
                    defaultIndeterminate={item.isDisabledRuleAppliedIndeterminant}
                    checked={item.isDisabledRuleApplied}
                />
            </Stack>
        );
    };

    const appliedTableColumns: IColumn[] = [
        {
            key: "name",
            name: "Setting Name",
            fieldName: "settingName",
            minWidth: 200,
            maxWidth: 200,
            isResizable: true,
            className: styles.appliedToTable,
            isPadded: true,
            onRenderHeader: props => {
                return (
                    <HuxControlLabel
                        infoText="Name of the setting to make hidden or disabled when the rule is applied."
                        labelText={props?.column.name}
                    />
                );
            }
        },
        {
            key: "hidden",
            name: "Hidden",
            fieldName: "isHiddenRuleApplied",
            minWidth: 50,
            maxWidth: 50,
            onRender: onRenderHidden,
            isPadded: true,
            onRenderHeader: props => {
                return (
                    <HuxControlLabel
                        infoText="Check to hide the setting when the rule is applied."
                        labelText={props?.column.name}
                    />
                );
            }
        },
        {
            key: "disabled",
            name: "Disabled",
            fieldName: "isDisabledRuleApplied",
            minWidth: 50,
            maxWidth: 50,
            onRender: onRenderDisabled,
            isPadded: true,
            className: styles.appliedToDisabled,
            onRenderHeader: props => {
                return (
                    <HuxControlLabel
                        infoText="Check to disable the setting when the rule is applied."
                        labelText={props?.column.name}
                    />
                );
            }
        },
        {
            key: "empty",
            name: "",
            minWidth: 10,
            maxWidth: 10
        }
    ];

    const footerProps: IPanelFooterProps = {
        onSave: onSaveRule,
        onCancel: vm.state.rulePanelProps.onClosePanel,
        isNew: vm.state.data.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid
    };

    return (
        <Panel
            isOpen={vm.state.rulePanelProps.isOpen}
            onDismiss={vm.state.rulePanelProps.onClosePanel}
            headerText={vm.state.rulePanelProps.panelTitle}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => {
                return PanelFooter(footerProps);
            }}
            isFooterAtBottom={true}
            type={PanelType.medium}
        >
            <Stack tokens={stackTokens}>
                <HuxTextBox
                    label="Name"
                    name="ruleName"
                    required
                    value={vm.state.data.ruleName}
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    disabled={!vm.state.rulePanelProps.data.isNew}
                    errorMessage={vm.state.touched.ruleName ? vm.state.errors.ruleName : ""}
                    onBlur={e => dispatch(vm.cmds.onBlur(e.currentTarget.name))}
                />

                <Dropdown
                    label="Target Setting"
                    required
                    selectedKey={vm.state.data.targetSettingName}
                    errorMessage={
                        vm.state.touched.targetSettingName ? vm.state.errors.targetSettingName : ""
                    }
                    options={vm.state.rulePanelProps.definition.Fields?.map(item => ({
                        key: item.SettingName,
                        text: item.SettingName
                    }))}
                    onBlur={() => vm.dispatch(vm.cmds.onBlur("targetSettingName"))}
                    onChange={(_, selected) =>
                        dispatch(
                            cmds.onTargetSettingChange(
                                selected !== undefined ? selected.key.toString() : ""
                            )
                        )
                    }
                />

                <HuxTextBox
                    label="Regex"
                    name="regex"
                    required
                    value={vm.state.data.regex}
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    errorMessage={vm.state.touched.regex ? vm.state.errors.regex : ""}
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                />

                <Label>Apply To</Label>

                <DetailsList
                    items={
                        vm.state.data.appliedToSettings?.filter(i => i.hideInList === false) ?? []
                    }
                    setKey="settingName"
                    getKey={item => item.settingName}
                    columns={appliedTableColumns}
                    layoutMode={DetailsListLayoutMode.justified}
                    checkButtonAriaLabel="Select applied to setting row"
                    selectionMode={SelectionMode.none}
                />
            </Stack>
        </Panel>
    );
};

export { RulePanel };
